import React, { useState, useEffect, useCallback } from 'react';
import { View, StyleSheet, FlatList, TouchableOpacity, RefreshControl, Alert } from 'react-native';
import { Text, Card, Searchbar, ActivityIndicator, Chip, Divider, FAB, Button } from 'react-native-paper';
import { useNavigation } from '@react-navigation/native';
import { Ionicons } from '@expo/vector-icons';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

const CierresScreen = () => {
  const navigation = useNavigation();
  const { user } = useAuth();
  const insets = useSafeAreaInsets();
  const [cierres, setCierres] = useState([]);
  const [filteredCierres, setFilteredCierres] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [filterEstado, setFilterEstado] = useState('todos');
  const [filterTipo, setFilterTipo] = useState('todos');

  const loadCierres = useCallback(async () => {
    if (!user) { setLoading(false); return; }
    try {
      const data = await api.getCierres();
      setCierres(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error al cargar cierres:', error);
      setCierres([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [user]);

  useEffect(() => { loadCierres(); }, [loadCierres]);

  useEffect(() => {
    let filtered = [...cierres];
    if (filterEstado !== 'todos') {
      filtered = filtered.filter(c => c.estado === filterEstado);
    }
    if (filterTipo !== 'todos') {
      filtered = filtered.filter(c => c.tipo === filterTipo);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(c =>
        (c.fecha || '').includes(q) ||
        (c.vendedor || c.usuario_nombre || '').toLowerCase().includes(q)
      );
    }
    setFilteredCierres(filtered);
  }, [cierres, filterEstado, filterTipo, searchQuery]);

  const ESTADOS = {
    completado: { bg: '#28A745', textColor: '#fff', label: 'Completado', icon: 'checkmark-circle' },
    pendiente:  { bg: '#FFC107', textColor: '#000', label: 'Pendiente',  icon: 'time' },
    rechazado:  { bg: '#DC3545', textColor: '#fff', label: 'Rechazado',  icon: 'close-circle' },
    abierto:    { bg: '#17A2B8', textColor: '#fff', label: 'Abierto',    icon: 'lock-open' },
  };
  const getEstadoInfo = (estado) => ESTADOS[estado] || { bg: '#6C757D', textColor: '#fff', label: estado || 'Pendiente', icon: 'ellipse' };

  const getTipoIcon = (tipo) => {
    switch (tipo) {
      case 'repartidor': return 'car';
      case 'consolidado': return 'analytics';
      default: return 'cart';
    }
  };

  const getTipoLabel = (tipo) => {
    switch (tipo) {
      case 'repartidor': return 'Repartidor';
      case 'consolidado': return 'Consolidado';
      default: return 'Vendedor';
    }
  };

  const canApprove = user && ['admin', 'supervisor', 'gerente'].includes(user.rol);

  const handleAprobarInline = (cierreId) => {
    Alert.alert('Aprobar Cierre', `¿Aprobar y completar el cierre #${cierreId}?`, [
      { text: 'Cancelar', style: 'cancel' },
      {
        text: 'Aprobar',
        onPress: async () => {
          try {
            await api.completarCierre(cierreId);
            loadCierres();
          } catch (error) {
            Alert.alert('Error', error.message || 'No se pudo aprobar el cierre');
          }
        },
      },
    ]);
  };

  const handleRechazarInline = (cierreId) => {
    Alert.alert('Rechazar Cierre', `¿Rechazar el cierre #${cierreId}?`, [
      { text: 'Cancelar', style: 'cancel' },
      {
        text: 'Rechazar',
        style: 'destructive',
        onPress: async () => {
          try {
            await api.rechazarCierre(cierreId, { motivo: 'Rechazado desde el listado' });
            loadCierres();
          } catch (error) {
            Alert.alert('Error', error.message || 'No se pudo rechazar el cierre');
          }
        },
      },
    ]);
  };

  const renderCierreItem = ({ item }) => {
    const est = getEstadoInfo(item.estado);
    const showInlineActions = canApprove && item.estado === 'pendiente';
    return (
    <TouchableOpacity onPress={() => navigation.navigate('DetalleCierre', { cierreId: item.id })}>
      <Card style={styles.card}>
        <Card.Content>
          <View style={styles.cardHeader}>
            <View style={styles.headerLeft}>
              <View style={styles.tipoRow}>
                <Ionicons name={getTipoIcon(item.tipo)} size={18} color="#0066cc" />
                <Text style={styles.tipoText}>{getTipoLabel(item.tipo)}</Text>
              </View>
              <Text style={styles.fechaText}>{item.fecha}</Text>
              <Text style={styles.vendedorText}>{item.vendedor || item.usuario_nombre}</Text>
            </View>
            <View style={styles.headerRight}>
              <View style={[styles.estadoChip, { backgroundColor: est.bg }]}>
                <Ionicons name={est.icon} size={12} color={est.textColor} />
                <Text style={[styles.estadoChipText, { color: est.textColor }]}>{est.label}</Text>
              </View>
              {item.es_automatico === 1 && (
                <View style={styles.autoChip}>
                  <Ionicons name="timer-outline" size={11} color="#1565C0" />
                  <Text style={styles.autoChipText}>Auto</Text>
                </View>
              )}
            </View>
          </View>
          <Divider style={styles.divider} />
          <View style={styles.montoContainer}>
            <View style={styles.montoItem}>
              <Text style={styles.montoLabel}>Ventas</Text>
              <Text style={styles.montoValue}>L. {(item.totalVentas || item.total_ventas || 0).toFixed(2)}</Text>
            </View>
            <View style={styles.montoItem}>
              <Text style={styles.montoLabel}>Devoluciones</Text>
              <Text style={[styles.montoValue, { color: '#f44336' }]}>-L. {(item.totalDevoluciones || item.total_devoluciones || 0).toFixed(2)}</Text>
            </View>
            <View style={styles.montoItem}>
              <Text style={styles.montoLabel}>Neto</Text>
              <Text style={[styles.montoValue, { color: '#4caf50', fontSize: 16 }]}>L. {(item.totalNeto || item.total_neto || 0).toFixed(2)}</Text>
            </View>
          </View>
          {item.arqueo_realizado === 1 && (
            <View style={styles.arqueoTag}>
              <Ionicons name="calculator" size={14} color="#6a1b9a" />
              <Text style={styles.arqueoTagText}>Arqueo: L. {(item.total_arqueo || 0).toFixed(2)}</Text>
              {item.diferencia_arqueo !== 0 && (
                <Text style={[styles.arqueoTagText, { color: item.diferencia_arqueo > 0 ? '#4caf50' : '#f44336' }]}>
                  ({item.diferencia_arqueo > 0 ? '+' : ''}{(item.diferencia_arqueo || 0).toFixed(2)})
                </Text>
              )}
            </View>
          )}

          {showInlineActions && (
            <View style={styles.inlineActions}>
              <Button
                mode="contained"
                compact
                icon="check"
                onPress={() => handleAprobarInline(item.id)}
                style={styles.approveBtn}
                labelStyle={styles.inlineBtnLabel}
              >
                Aprobar
              </Button>
              <Button
                mode="outlined"
                compact
                icon="close"
                onPress={() => handleRechazarInline(item.id)}
                style={styles.rejectBtn}
                labelStyle={[styles.inlineBtnLabel, { color: '#f44336' }]}
              >
                Rechazar
              </Button>
            </View>
          )}
        </Card.Content>
      </Card>
    </TouchableOpacity>
  );};

  const canCreate = user && ['admin', 'supervisor', 'gerente', 'vendedor', 'repartidor'].includes(user.rol);

  return (
    <View style={styles.container}>
      <Searchbar
        placeholder="Buscar por fecha o nombre"
        onChangeText={setSearchQuery}
        value={searchQuery}
        style={styles.searchBar}
      />

      <View style={styles.filterContainer}>
        <Text style={styles.filterLabel}>Estado:</Text>
        <View style={styles.chipRow}>
          {['todos', 'pendiente', 'completado', 'rechazado'].map(e => (
            <Chip
              key={e}
              onPress={() => setFilterEstado(e)}
              style={[styles.filterChip, filterEstado === e && styles.filterChipActive]}
              textStyle={filterEstado === e ? styles.filterChipTextActive : styles.filterChipTextInactive}
            >
              {e === 'todos' ? 'Todos' : e.charAt(0).toUpperCase() + e.slice(1)}
            </Chip>
          ))}
        </View>
      </View>

      <View style={styles.filterContainer}>
        <Text style={styles.filterLabel}>Tipo:</Text>
        <View style={styles.chipRow}>
          {['todos', 'vendedor', 'repartidor', 'consolidado'].map(t => (
            <Chip
              key={t}
              onPress={() => setFilterTipo(t)}
              style={[styles.filterChip, filterTipo === t && styles.filterChipActive]}
              textStyle={filterTipo === t ? styles.filterChipTextActive : styles.filterChipTextInactive}
            >
              {t === 'todos' ? 'Todos' : getTipoLabel(t)}
            </Chip>
          ))}
        </View>
      </View>

      {loading ? (
        <View style={styles.loaderContainer}>
          <ActivityIndicator size="large" color="#0066cc" />
          <Text style={styles.loaderText}>Cargando cierres...</Text>
        </View>
      ) : (
        <FlatList
          data={filteredCierres}
          renderItem={renderCierreItem}
          keyExtractor={(item) => item.id.toString()}
          contentContainerStyle={styles.listContainer}
          refreshControl={<RefreshControl refreshing={refreshing} onRefresh={() => { setRefreshing(true); loadCierres(); }} />}
          ListEmptyComponent={
            <View style={styles.emptyContainer}>
              <Ionicons name="document-text-outline" size={64} color="#ccc" />
              <Text style={styles.emptyText}>No se encontraron cierres</Text>
            </View>
          }
        />
      )}

      {canCreate && (
        <FAB
          style={[styles.fab, { marginBottom: insets.bottom }]}
          icon="plus"
          label="Nuevo Cierre"
          onPress={() => navigation.navigate('NuevoCierre')}
        />
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#f5f5f5', padding: 10 },
  searchBar: { marginBottom: 8, elevation: 2 },
  filterContainer: { marginBottom: 6 },
  filterLabel: { fontSize: 12, color: '#666', marginBottom: 4 },
  chipRow: { flexDirection: 'row', flexWrap: 'wrap' },
  filterChip: { marginRight: 6, marginBottom: 4, backgroundColor: '#f0f0f0' },
  filterChipActive: { backgroundColor: '#4CAF50' },
  filterChipTextInactive: { color: '#333' },
  filterChipTextActive: { color: 'white', fontWeight: '600' },
  listContainer: { paddingBottom: 80 },
  card: { marginBottom: 10, elevation: 2 },
  cardHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start' },
  headerLeft: { flex: 1 },
  headerRight: { alignItems: 'flex-end' },
  tipoRow: { flexDirection: 'row', alignItems: 'center', marginBottom: 4 },
  tipoText: { fontSize: 12, color: '#0066cc', fontWeight: '600', marginLeft: 4 },
  fechaText: { fontSize: 16, fontWeight: 'bold' },
  vendedorText: { fontSize: 14, color: '#666' },
  estadoChip: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 8, paddingVertical: 3, borderRadius: 12, marginBottom: 4, gap: 4 },
  estadoChipText: { fontSize: 10, fontWeight: 'bold' },
  autoChip: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 7, paddingVertical: 2, borderRadius: 10, backgroundColor: '#e3f2fd', gap: 3 },
  autoChipText: { fontSize: 10, color: '#1565C0', fontWeight: '600' },
  divider: { marginVertical: 8 },
  montoContainer: { flexDirection: 'row', justifyContent: 'space-between' },
  montoItem: { flex: 1, alignItems: 'center' },
  montoLabel: { fontSize: 11, color: '#666', marginBottom: 2 },
  montoValue: { fontSize: 14, fontWeight: 'bold' },
  arqueoTag: { flexDirection: 'row', alignItems: 'center', marginTop: 8, backgroundColor: '#f3e5f5', padding: 6, borderRadius: 4 },
  arqueoTagText: { fontSize: 12, color: '#6a1b9a', marginLeft: 4, fontWeight: '600' },
  fab: { position: 'absolute', margin: 16, right: 0, bottom: 0, backgroundColor: '#007bff' },
  inlineActions: { flexDirection: 'row', justifyContent: 'flex-end', gap: 8, marginTop: 10 },
  approveBtn: { backgroundColor: '#28a745' },
  rejectBtn: { borderColor: '#f44336' },
  inlineBtnLabel: { fontSize: 12, marginVertical: 4 },
  loaderContainer: { flex: 1, justifyContent: 'center', alignItems: 'center' },
  loaderText: { marginTop: 10, color: '#666' },
  emptyContainer: { flex: 1, justifyContent: 'center', alignItems: 'center', paddingTop: 50 },
  emptyText: { fontSize: 16, color: '#666', marginTop: 16 },
});

export default CierresScreen;
